import { toUppercase } from "../../common/helperfunctions/stringFunctions";
import { Table } from "../../common/panels/Detail/DetailTable";
import { NotAvailable, NotSet } from "../../common/misc/UIconstants";
import { EntityDetailProps, EntityDetailTableProps } from "../../common/entity/EntityInterfaces";
import Status from "../../common/badges/Status/Status";
import Detail from "../../common/panels/Detail/Detail";
import { DetailsPageTopControlsView } from "../../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { DatasetsTile } from "../../common/sidebar/tiles/DatasetsTile";
import { CustomTypeTile } from "../../common/sidebar/tiles/CustomTypeTile";
import { SamplesTile } from "../../common/sidebar/tiles/SamplesTile";
import { InventoriesTile } from "../../common/sidebar/tiles/InventoriesTile";
import { CustomFieldRenderer } from "./CustomFieldRenderer";
import { ProjectsTile } from "../../common/sidebar/tiles/ProjectsTile";
import { PersonsTile } from "../../common/sidebar/tiles/PersonsTile";
import { generateAbstractedFromDataType } from "./CustomFieldUtils";
import { AbstractedCustomFieldDataTypeIcon } from "./CustomFieldRenderUtils";
import { RenderMinimalEntity, TableArrayRenderer } from "../../common/misc/EntityRenders/EntityRenderer";
import { customTypeConstants } from "../../api/CustomTypes/CustomTypes";
import { ToggleIndicator } from "../../common/badges/ToggleIndicator/ToggleIndicator";
import { CustomFieldDataTypeUtils } from "../../api/CustomFields/CustomFieldsBase";
import { EntitySidebarTileAutosorter } from "../../common/sidebar/tiles/EntitySidebarTile/EntitySidebarTileAutosorter";
import { EntityDeleteButton } from "../../Entities/GenericComponents/common/GenericEntityDetailPage";
import { EntityEditButton } from "../../common/entity/entityComponents/EntityEditButton";
import { EntityCloneButton } from "../../common/entity/entityComponents/EntityCloneButton";
import { LinkEntity } from "../../common/misc/LinkEntity/LinkEntity";
import { customFieldTemplateConstants } from "../../api/CustomFields/CustomFieldTemplates";
import { useContext } from "react";
import { SessionContext } from "../../common/contexts/SessionContext";
import { getIndexRoute } from "../../main/Routing";

export const CustomFieldsDetailTable = ({
  permissions,
  routes,
  entity,
  nonFoldable = true,
  entityConstants,
  fieldLabels,
}: EntityDetailTableProps<"customFields"> & { nonFoldable?: boolean }) => {
  const { route } = useContext(SessionContext);
  const abstractedDataType = generateAbstractedFromDataType(entity.dataType);
  return (
    <>
      <Table noPadding nonFoldable={nonFoldable}>
        <Table.Head>Details</Table.Head>
        <Table.Body>
          <Table.Body.RowContent
            title={fieldLabels.name}
            content={
              <div className="container_label">
                <div className="container_label_name" style={{ fontWeight: 600 }}>
                  {entity.name ?? NotSet}
                </div>
                <div className="container_label_id">
                  <span style={{ whiteSpace: "nowrap" }}>
                    {toUppercase(entityConstants.entitySingular)}-ID: {entity.id ?? NotAvailable}
                  </span>
                </div>
              </div>
            }
          />
          <Table.Body.RowContent
            title={fieldLabels.dataType}
            content={
              abstractedDataType.abstractedDataType ? (
                <div className="flex row-nowrap align-center gap-5">
                  <AbstractedCustomFieldDataTypeIcon dataType={abstractedDataType.abstractedDataType} />
                  {abstractedDataType.abstractedDataType}
                </div>
              ) : (
                NotSet
              )
            }
          />
          {abstractedDataType.abstractedDataType === "Entity Reference" && (
            <Table.Body.RowContent
              title={"Reference type"}
              content={
                abstractedDataType.entitySelectOption ? (
                  <div className="flex row-nowrap align-center gap-5">
                    <LucideIcon
                      name={
                        CustomFieldDataTypeUtils.MapAbstractedEntityReferenceTypeToEntityConstants(
                          abstractedDataType.entitySelectOption
                        ).icon
                      }
                      color="var(--primary)"
                    />
                    {abstractedDataType.entitySelectOption}
                  </div>
                ) : (
                  NotSet
                )
              }
            />
          )}
          {Array.isArray(entity.customTypeConstraint) && !!entity.customTypeConstraint.length && (
            <Table.Body.RowContent
              title={fieldLabels.customTypeConstraint}
              content={
                <RenderMinimalEntity
                  entities={entity.customTypeConstraint}
                  entityConstants={customTypeConstants}
                  showLinks={true}
                />
              }
            />
          )}

          <Table.Body.RowContent
            title={fieldLabels.description}
            content={
              <>
                {entity.description ? (
                  <div className="container_notes">
                    <textarea rows={4} className="container_textarea" value={entity?.description} disabled />
                  </div>
                ) : (
                  NotSet
                )}
              </>
            }
          />
          <Table.Body.RowContent
            title={fieldLabels.placeholder}
            content={
              <>
                {entity.placeholder ? (
                  <div className="ellipsisContainer">
                    <span>{entity?.placeholder}</span>
                  </div>
                ) : (
                  NotSet
                )}
              </>
            }
          />
          {CustomFieldDataTypeUtils.HasMultiSelect(entity.dataType) && (
            <Table.Body.RowContent
              title={fieldLabels.isMultiValue}
              content={<ToggleIndicator enabled={!!abstractedDataType.isMulti} />}
            />
          )}
          {CustomFieldDataTypeUtils.IsNumberDataType(entity.dataType) && (
            <Table.Body.RowContent
              title={fieldLabels.isFloat}
              content={<ToggleIndicator enabled={!!abstractedDataType.isFloat} />}
            />
          )}
          {abstractedDataType.abstractedDataType === "Date" && (
            <Table.Body.RowContent
              title={fieldLabels.isDateTime}
              content={<ToggleIndicator enabled={!!abstractedDataType.isDateTime} />}
            />
          )}
          {abstractedDataType.abstractedDataType === "Date" && abstractedDataType.isDateTimeRange && (
            <Table.Body.RowContent
              title={fieldLabels.isDateTimeRange}
              content={<ToggleIndicator enabled={!!abstractedDataType.isDateTimeRange} />}
            />
          )}
          {abstractedDataType.abstractedDataType === "Time" && abstractedDataType.isTimeRange && (
            <Table.Body.RowContent
              title={fieldLabels.isTimeRange}
              content={<ToggleIndicator enabled={!!abstractedDataType.isTimeRange} />}
            />
          )}

          {entity.dataType === "String" && (
            <Table.Body.RowContent
              title={fieldLabels.showAsTextArea}
              content={
                <span className="badge">
                  <Status type={entity.showAsTextArea ? "success" : "danger"}>
                    {entity.showAsTextArea ? "on" : "off"}
                  </Status>
                </span>
              }
            />
          )}

          {entity.dataType === "Barcode" && (
            <Table.Body.RowContent
              title={fieldLabels.useUUID}
              content={
                <span className="badge">
                  <Status type={entity.useUUID ? "success" : "danger"}>{entity.useUUID ? "on" : "off"}</Status>
                </span>
              }
            />
          )}
          <Table.Body.RowContent
            title={fieldLabels.required}
            content={
              <span className="badge">
                <Status type={entity.required ? "success" : "danger"}>{entity.required ? "on" : "off"}</Status>
              </span>
            }
          />
          <Table.Body.RowContent
            title={fieldLabels.readOnly}
            content={
              <span className="badge">
                <Status type={entity.readOnly ? "success" : "danger"}>{entity.readOnly ? "on" : "off"}</Status>
              </span>
            }
          />
          <Table.Body.RowContent title={fieldLabels.validationRegexp} content={entity.validationRegexp || NotSet} />
          <Table.Body.RowContent title={fieldLabels.validationMessage} content={entity.validationMessage || NotSet} />

          {CustomFieldDataTypeUtils.IsCustomizibleRegexDataType(entity.dataType) && (
            <>
              {entity.predefinedOptionsFromValues ? (
                <Table.Body.RowContent
                  title={fieldLabels.predefinedOptionsFromValues}
                  content={
                    <span className="badge">
                      <Status type={entity.predefinedOptionsFromValues ? "success" : "danger"}>
                        {entity.predefinedOptionsFromValues ? "on" : "off"}
                      </Status>
                    </span>
                  }
                />
              ) : (
                <Table.Body.RowContent
                  title={fieldLabels.predefinedOptions}
                  content={
                    entity.predefinedOptions ? (
                      <div className="flex row-nowrap align-center gap-5">
                        {entity.predefinedOptions.map((e, i) => (
                          <label className={"label label-soft-info"} style={{ margin: 0 }} key={i}>
                            {e}
                          </label>
                        ))}
                      </div>
                    ) : (
                      NotSet
                    )
                  }
                />
              )}
            </>
          )}
          <CustomFieldRenderer
            title={fieldLabels.defaultValues}
            customfield={entity}
            value={entity.defaultValues}
            includeCustomFieldId={false}
          />
        </Table.Body>
      </Table>
      {!!entity.customFieldTemplate && (
        <Table>
          <Table.Head>Template information</Table.Head>
          <Table.Body.RowContent
            title={fieldLabels.customFieldTemplate}
            content={
              <LinkEntity
                entityConstants={customFieldTemplateConstants}
                property={entity.customFieldTemplate}
                showLink={true}
                labelOverride={
                  entity.customFieldTemplate.name +
                  (entity.templateVersion ? ` (Version: ${entity.templateVersion})` : "")
                }
                routeOverride={route(getIndexRoute(customFieldTemplateConstants.frontendIndexRoute))}
              />
            }
          />
        </Table>
      )}

      {!!entity.integrations?.length && (
        <Table>
          <Table.Head>Integration usage</Table.Head>
          <Table.Body.RowContent
            title={fieldLabels.integrations}
            content={
              <TableArrayRenderer values={entity.integrations} arrayLength={5}>
                {(e) => <span className="label label-soft-default">{e}</span>}
              </TableArrayRenderer>
            }
          />
        </Table>
      )}
    </>
  );
};

export const CustomFieldsDetail = ({
  entity,
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  entityTypeId,
}: EntityDetailProps<"customFields">) => {
  return (
    <Detail
      head={
        <DetailsPageTopControlsView
          entityTypeId={entityTypeId}
          entity={entity}
          entityConstants={entityConstants}
          controls={
            <>
              <EntityEditButton
                entity={entity}
                entityConstants={entityConstants}
                permissions={permissions}
                routes={routes}
              />
            </>
          }
          controlsDropdown={
            <>
              <li>
                <EntityCloneButton
                  entity={entity}
                  entityConstants={entityConstants}
                  routes={routes}
                  permissions={permissions}
                />
              </li>
              <li className="divider" />
              <li>
                <EntityDeleteButton entity={entity} entityTypeId={entityTypeId} alwaysDeletePermanently />
              </li>
            </>
          }
        />
      }
      mainStyle={{ padding: "0px 10px" }}
      main={
        <>
          <CustomFieldsDetailTable
            entityTypeId={entityTypeId}
            entity={entity}
            entityConstants={entityConstants}
            fieldLabels={fieldLabels}
            permissions={permissions}
            routes={routes}
          />
          {/* <RenderCustomFieldPreview initialValues={customField} dataType={customField.dataType} /> */}
        </>
      }
      sidebar={
        <EntitySidebarTileAutosorter>
          {/* TODO */}
          <CustomTypeTile
            title="Used in custom types"
            defaultFilters={{ customFieldIds: [entity.id] }}
            excludeFilters={{ customFieldIds: () => false }}
          />
          <SamplesTile
            title="Used in samples"
            defaultFilters={{ customFieldIds: [entity.id] }}
            excludeFilters={{ customFieldIds: () => false }}
          />
          <DatasetsTile
            title="Used in datasets"
            defaultFilters={{ customFieldIds: [entity.id] }}
            excludeFilters={{ customFieldIds: () => false }}
          />
          <InventoriesTile
            title="Used in inventory items"
            defaultFilters={{ customFieldIds: [entity.id] }}
            excludeFilters={{ customFieldIds: () => false }}
          />
          <ProjectsTile
            title="Used in projects"
            defaultFilters={{ customFieldIds: [entity.id] }}
            excludeFilters={{ customFieldIds: () => false }}
          />
          <PersonsTile
            title="Used in persons"
            defaultFilters={{ customFieldIds: [entity.id] }}
            excludeFilters={{ customFieldIds: () => false }}
          />
        </EntitySidebarTileAutosorter>
      }
    />
  );
};
